import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import BackdropCircularProgress from '../../components/BackdropCircularProgress';
import JobCreationFAQ from '../../components/FAQcomponent';
import FormCloseButton from '../../components/Form/FormCloseButton';
import selectUser from '../../store/selectors/appSelector';
import { showSnackbar } from '../app';
import { ROUTE_CONSTANTS } from '../app/RouteBuilding/routeConstants';

import CreateJobFormComponent from './BulkDayJobCreateCard/CreateJobFormComponent';
import { FAQ_LIST } from './constDicts';
import { validateJobCreation } from './helpers';
import {
  clearBulkDayJobError,
  clearPostBulkDayJobResponse,
  postBulkDayJob,
} from './jobCreateReducer';
import {
  selectErrorPostBulkDayJob,
  selectIsProcessingPostBulkDayJob,
  selectPostBulkDayJobResponse,
} from './jobCreateSelectors';

const BulkDayJobCreate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const theme = useTheme();

  const isProcessingPostBulkDayJob = useSelector(selectIsProcessingPostBulkDayJob);
  const error = useSelector(selectErrorPostBulkDayJob);
  const createResponse = useSelector(selectPostBulkDayJobResponse);

  const [jobName, setJobName] = useState(null);

  const submitFunc = (formValues) => {
    // Validate job creation data
    const validation = validateJobCreation(formValues.datesOpenings);
    if (!validation.isValid) {
      dispatch(showSnackbar({ message: validation.message }));
      return;
    }

    // Format data for the new API
    const jobOrderInfo = formValues.datesOpenings.map((item) => {
      const date = new Date(item.date);
      const startTimeMinutes = formValues.startTime?.value || 0;
      const endTimeMinutes = formValues.endTime?.value || 0;

      // Calculate start and end times in epoch milliseconds
      const start = new Date(date);
      start.setHours(Math.floor(startTimeMinutes / 60), startTimeMinutes % 60, 0, 0);

      const end = new Date(date);
      end.setHours(Math.floor(endTimeMinutes / 60), endTimeMinutes % 60, 0, 0);

      // If end time is before start time, it's an overnight shift
      if (end < start) {
        end.setDate(end.getDate() + 1);
      }

      return {
        start: start.getTime(),
        end: end.getTime(),
        positionTemplateId: formValues.positionTemplate?._id,
        departmentId: formValues.department?._id,
        locationId: formValues.location?._id,
        numOpenings: item.openings,
      };
    });

    const createPayload = {
      jobOrderInfo,
    };

    setJobName(formValues?.positionTemplate?.name);
    dispatch(postBulkDayJob(createPayload));
  };

  const closeAndClear = useCallback(
    (message) => {
      navigate(`/${ROUTE_CONSTANTS.JOB_ORDERS}`);
      dispatch(clearPostBulkDayJobResponse());
      dispatch(showSnackbar({ message }));
    },
    [dispatch, navigate]
  );

  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const mainWrapperSx = {
    display: 'flex',
    padding: theme.spacing(2),
    flexDirection: smallScreen ? 'column' : 'row',
    gap: theme.spacing(2),
  };

  const message = LanguageConverter('job.create.success', jobName);

  useEffect(() => {
    if (createResponse?.data?.jobOrderIds) {
      closeAndClear(message);
    }
  }, [closeAndClear, createResponse, dispatch, jobName, message]);

  useEffect(() => {
    if (error) {
      dispatch(showSnackbar({ message: error }));
      dispatch(clearBulkDayJobError());
    }
  }, [dispatch, error]);

  return (
    <Box
      sx={{
        position: 'absolute',
        zIndex: 10,
        width: '100vw',
        padding: theme.spacing(2),
      }}
    >
      {isProcessingPostBulkDayJob && <BackdropCircularProgress />}
      <FormCloseButton onClose={closeAndClear} message="jobs.backToList" />
      {createResponse === null && (
        <Box sx={mainWrapperSx}>
          <Box
            sx={{
              flex: 5,
            }}
          >
            <Typography
              sx={{
                color: theme?.jobOrders?.createPage?.addJobHeaderTypography?.fontColor,
                fontSize: theme?.jobOrders?.createPage?.addJobHeaderTypography?.fontSize,
                fontFamily: theme?.jobOrders?.createPage?.addJobHeaderTypography?.font,
                marginBottom: theme.spacing(1),
              }}
            >
              {LanguageConverter('job.create')}
            </Typography>
            <CreateJobFormComponent
              user={user}
              onSubmit={submitFunc}
              onClose={closeAndClear}
              isProcessing={isProcessingPostBulkDayJob}
            />
          </Box>

          <Box sx={{ flex: 2 }}>
            <JobCreationFAQ faqList={FAQ_LIST} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BulkDayJobCreate;
